import React, { createContext, useContext, useState, useReducer, useCallback, useEffect } from 'react';

const DevisContext = createContext();

// Initial state for the quotes reducer
const initialState = {
  quotes: [],
  loading: true,
  error: null,
  processingQuotes: [],
  selectedQuoteId: null,
  quoteToDelete: null,
  selectedImage: null,
  searchTerm: '',
  currentPage: 1,
  itemsPerPage: 6,
  selectedQuotes: [],
  expandedQuantities: {},
  hoveredEmail: null,
  totalPages: 0,
  filteredQuotes: [],
  confirmLoading: null,
  rejectionReason: "",
  logoPriceValue: '',
  showDetailsModal: false,
  selectedQuoteDetails: null,
  loadingQuoteDetails: false
};

// Action types
const FETCH_QUOTES_START = 'FETCH_QUOTES_START';
const FETCH_QUOTES_SUCCESS = 'FETCH_QUOTES_SUCCESS';
const FETCH_QUOTES_ERROR = 'FETCH_QUOTES_ERROR';
const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';
const SET_ITEMS_PER_PAGE = 'SET_ITEMS_PER_PAGE';
const TOGGLE_QUANTITY_EXPANSION = 'TOGGLE_QUANTITY_EXPANSION';
const SET_SELECTED_QUOTE_ID = 'SET_SELECTED_QUOTE_ID';
const SET_QUOTE_TO_DELETE = 'SET_QUOTE_TO_DELETE';
const SET_SELECTED_IMAGE = 'SET_SELECTED_IMAGE';
const SET_HOVERED_EMAIL = 'SET_HOVERED_EMAIL';
const SET_PROCESSING_QUOTES = 'SET_PROCESSING_QUOTES';
const SET_CONFIRM_LOADING = 'SET_CONFIRM_LOADING';
const UPDATE_QUOTE_STATUS = 'UPDATE_QUOTE_STATUS';
const DELETE_QUOTE = 'DELETE_QUOTE';
const SET_REJECTION_REASON = 'SET_REJECTION_REASON';
const SET_FILTERED_QUOTES = 'SET_FILTERED_QUOTES';
const SET_TOTAL_PAGES = 'SET_TOTAL_PAGES';
const SET_SELECTED_QUOTES = 'SET_SELECTED_QUOTES';
const SET_LOGO_PRICE_VALUE = 'SET_LOGO_PRICE_VALUE';
const CLEAR_ERROR = 'CLEAR_ERROR';
const SET_SHOW_DETAILS_MODAL = 'SET_SHOW_DETAILS_MODAL';
const SET_SELECTED_QUOTE_DETAILS = 'SET_SELECTED_QUOTE_DETAILS';
const SET_LOADING_QUOTE_DETAILS = 'SET_LOADING_QUOTE_DETAILS';

// Reducer function to handle state updates
const devisReducer = (state, action) => {
  switch (action.type) {
    case FETCH_QUOTES_START:
      return { ...state, loading: true, error: null };
    
    case FETCH_QUOTES_SUCCESS:
      return { ...state, quotes: action.payload, loading: false };
    
    case FETCH_QUOTES_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case SET_SEARCH_TERM:
      return { ...state, searchTerm: action.payload, currentPage: 1, selectedQuotes: [] };
    
    case SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    
    case SET_ITEMS_PER_PAGE:
      return { ...state, itemsPerPage: action.payload };
    
    case TOGGLE_QUANTITY_EXPANSION:
      return { 
        ...state, 
        expandedQuantities: {
          ...state.expandedQuantities,
          [action.payload]: !state.expandedQuantities[action.payload]
        }
      };
    
    case SET_SELECTED_QUOTE_ID:
      return { ...state, selectedQuoteId: action.payload };
    
    case SET_QUOTE_TO_DELETE:
      return { ...state, quoteToDelete: action.payload };
    
    case SET_SELECTED_IMAGE:
      return { ...state, selectedImage: action.payload };
    
    case SET_HOVERED_EMAIL:
      return { ...state, hoveredEmail: action.payload };
    
    case SET_SHOW_DETAILS_MODAL:
      return { ...state, showDetailsModal: action.payload };
    
    case SET_SELECTED_QUOTE_DETAILS:
      return { ...state, selectedQuoteDetails: action.payload };
    
    case SET_LOADING_QUOTE_DETAILS:
      return { ...state, loadingQuoteDetails: action.payload };
    
    case SET_PROCESSING_QUOTES:
      if (action.operation === 'add') {
        return { 
          ...state, 
          processingQuotes: [...state.processingQuotes, action.payload] 
        };
      } else if (action.operation === 'remove') {
        return { 
          ...state, 
          processingQuotes: state.processingQuotes.filter(id => id !== action.payload) 
        };
      }
      return state;
    
    case SET_CONFIRM_LOADING:
      return { ...state, confirmLoading: action.payload };
    
    case UPDATE_QUOTE_STATUS:
      return {
        ...state,
        quotes: state.quotes.map(quote => 
          quote.id === action.payload.id 
            ? { ...quote, ...action.payload.updates } 
            : quote
        )
      };
    
    case DELETE_QUOTE:
      return {
        ...state,
        quotes: state.quotes.filter(quote => quote.id !== action.payload)
      };
    
    case SET_REJECTION_REASON:
      return { ...state, rejectionReason: action.payload };
    
    case SET_FILTERED_QUOTES:
      return { ...state, filteredQuotes: action.payload };
    
    case SET_TOTAL_PAGES:
      return { ...state, totalPages: action.payload };
    
    case SET_SELECTED_QUOTES:
      if (action.operation === 'all') {
        return { ...state, selectedQuotes: action.payload };
      } else if (action.operation === 'toggle') {
        return { 
          ...state, 
          selectedQuotes: state.selectedQuotes.includes(action.payload)
            ? state.selectedQuotes.filter(id => id !== action.payload)
            : [...state.selectedQuotes, action.payload]
        };
      }
      return state;
    
    case SET_LOGO_PRICE_VALUE:
      return { ...state, logoPriceValue: action.payload };
    
    case CLEAR_ERROR:
      return { ...state, error: null };
    
    default:
      return state;
  }
};

export const DevisProvider = ({ children }) => {
  const [state, dispatch] = useReducer(devisReducer, initialState);
  
  // UI state for modals
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showLogoPriceModal, setShowLogoPriceModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Function to set show details modal
  const setShowDetailsModal = (value) => {
    dispatch({ type: SET_SHOW_DETAILS_MODAL, payload: value });
    if (!value) {
      dispatch({ type: SET_SELECTED_QUOTE_DETAILS, payload: null });
    }
  };

  const API_BASE_URL = "https://api.greenecobagsusa.com/api";

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Auto hide error after 3 seconds
  useEffect(() => {
    if (state.error) {
      const timer = setTimeout(() => {
        dispatch({ type: CLEAR_ERROR });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state.error]);

  // Update filtered quotes when quotes or search term changes
  useEffect(() => {
    if (!state.quotes || !Array.isArray(state.quotes)) {
      dispatch({ type: SET_FILTERED_QUOTES, payload: [] });
      return;
    }

    const normalizedTerm = state.searchTerm.toLowerCase().trim();
    
    const filtered = normalizedTerm 
      ? state.quotes.filter((quote) => {
          const name = quote.name?.toLowerCase() || "";
          const email = quote.email?.toLowerCase() || "";
          const phone = quote.phone?.toLowerCase() || "";
          const status = quote.status?.toLowerCase() || "";

          return (
            name.includes(normalizedTerm) ||
            email.includes(normalizedTerm) ||
            phone.includes(normalizedTerm) ||
            status.includes(normalizedTerm)
          );
        })
      : state.quotes;
    
    dispatch({ type: SET_FILTERED_QUOTES, payload: filtered });
    
    // Update total pages
    const totalPages = Math.ceil(filtered.length / state.itemsPerPage);
    dispatch({ type: SET_TOTAL_PAGES, payload: totalPages });
    
    // Ensure current page is valid
    if (state.currentPage > totalPages && totalPages > 0) {
      dispatch({ type: SET_CURRENT_PAGE, payload: totalPages });
    }
  }, [state.quotes, state.searchTerm, state.itemsPerPage]);

  // API Utilities
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    };
  }, []);

  const handleApiError = useCallback((error) => {
    console.error("API Error:", error);
    if (error.response?.status === 401) {
      dispatch({ 
        type: FETCH_QUOTES_ERROR, 
        payload: "Session expirée. Veuillez vous reconnecter." 
      });
      localStorage.removeItem('token');
      return;
    }
    dispatch({ 
      type: FETCH_QUOTES_ERROR, 
      payload: error.message || "Une erreur est survenue" 
    });
  }, []);

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }

      return { success: true };
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [getHeaders, handleApiError]);

  // API Actions
  const fetchQuotes = useCallback(async () => {
    dispatch({ type: FETCH_QUOTES_START });
    try {
      const data = await apiRequest('/quotes');
      console.log('Fetched quotes:', data.quotes || []);
      dispatch({ type: FETCH_QUOTES_SUCCESS, payload: data.quotes || [] });
    } catch (error) {
      handleApiError(error);
      dispatch({ type: FETCH_QUOTES_SUCCESS, payload: [] }); // Fallback to avoid errors
    }
  }, [apiRequest, handleApiError]);
  
  // Function to fetch quote details
  const fetchQuoteDetails = useCallback(async (id) => {
    dispatch({ type: SET_LOADING_QUOTE_DETAILS, payload: true });
    try {
      const data = await apiRequest(`/quotes/${id}`);
      const quoteData = data.quote;
      
      // Ensure JSON fields are properly parsed
      if (typeof quoteData.sizes === 'string') {
        quoteData.sizes = JSON.parse(quoteData.sizes);
      }
      if (typeof quoteData.sizes_quantities === 'string') {
        quoteData.sizes_quantities = JSON.parse(quoteData.sizes_quantities);
      }
      if (typeof quoteData.logo_colors === 'string') {
        quoteData.logo_colors = JSON.parse(quoteData.logo_colors);
      }
      if (typeof quoteData.product_colors === 'string') {
        quoteData.product_colors = JSON.parse(quoteData.product_colors);
      }
      
      dispatch({ type: SET_SELECTED_QUOTE_DETAILS, payload: quoteData });
    } catch (error) {
      handleApiError(error);
      // Show error notification or handle error
    } finally {
      dispatch({ type: SET_LOADING_QUOTE_DETAILS, payload: false });
    }
  }, [apiRequest, handleApiError]);

  const confirmQuote = useCallback(async (id) => {
    try {
      const quote = state.quotes.find(q => q.id === id);

      if (quote?.is_logo_required && quote?.logo_path && !quote?.price_logo_quote) {
        setShowLogoPriceModal(true);
        dispatch({ type: SET_SELECTED_QUOTE_ID, payload: id });
        return;
      }

      dispatch({ type: SET_CONFIRM_LOADING, payload: id });

      const response = await fetch(`${API_BASE_URL}/quotes/${id}/confirm`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        await response.json();
        dispatch({ 
          type: UPDATE_QUOTE_STATUS, 
          payload: { id, updates: { status: 'confirmed' } } 
        });
      } else {
        const errorData = await response.json();
        console.error('Erreur lors de la confirmation du devis', errorData);
      }
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
    } finally {
      dispatch({ type: SET_CONFIRM_LOADING, payload: null });
    }
  }, [state.quotes, getHeaders]);

  const saveLogoPrice = useCallback(async () => {
    if (!state.logoPriceValue || parseFloat(state.logoPriceValue) <= 0) {
      console.error('Veuillez saisir un prix valide');
      return;
    }

    try {
      dispatch({ type: SET_CONFIRM_LOADING, payload: state.selectedQuoteId });

      const response = await fetch(`${API_BASE_URL}/quotes/${state.selectedQuoteId}/update-logo-price`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          price_logo_quote: parseFloat(state.logoPriceValue)
        })
      });

      const responseData = await response.json();

      if (response.ok || responseData.success) {
        // Fermer le modal
        setShowLogoPriceModal(false);
        dispatch({ type: SET_LOGO_PRICE_VALUE, payload: '' });

        // Confirmer le devis après avoir sauvé le prix
        await confirmQuoteAfterPrice(state.selectedQuoteId);

        // Rafraîchir les données
        await fetchQuotes();

        dispatch({ type: SET_SELECTED_QUOTE_ID, payload: null });
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      dispatch({ type: SET_CONFIRM_LOADING, payload: null });
    }
  }, [state.logoPriceValue, state.selectedQuoteId, fetchQuotes, getHeaders]);

  const confirmQuoteAfterPrice = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quotes/${id}/confirm`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        await response.json();
        dispatch({ 
          type: UPDATE_QUOTE_STATUS, 
          payload: { id, updates: { status: 'confirmed' } } 
        });
      } else {
        console.error('Erreur lors de la confirmation du devis');
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  }, [getHeaders]);

  const rejectQuote = useCallback(async () => {
    if (!state.selectedQuoteId) return;

    dispatch({ 
      type: SET_PROCESSING_QUOTES, 
      operation: 'add', 
      payload: state.selectedQuoteId 
    });
    
    setShowRejectModal(false);

    try {
      await apiRequest(`/quotes/${state.selectedQuoteId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: state.rejectionReason }),
      });

      dispatch({ 
        type: UPDATE_QUOTE_STATUS, 
        payload: { 
          id: state.selectedQuoteId, 
          updates: { 
            status: "rejected", 
            rejection_reason: state.rejectionReason || "Aucune raison spécifiée" 
          } 
        } 
      });
      
      dispatch({ type: SET_REJECTION_REASON, payload: "" });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ 
        type: SET_PROCESSING_QUOTES, 
        operation: 'remove', 
        payload: state.selectedQuoteId 
      });
    }
  }, [state.selectedQuoteId, state.rejectionReason, apiRequest, handleApiError]);

  const deleteQuote = useCallback(async () => {
    if (!state.quoteToDelete) return;

    dispatch({ 
      type: SET_PROCESSING_QUOTES, 
      operation: 'add', 
      payload: state.quoteToDelete 
    });
    
    setShowDeleteModal(false);

    try {
      await apiRequest(`/quotes/${state.quoteToDelete}`, {
        method: 'DELETE',
      });

      dispatch({ type: DELETE_QUOTE, payload: state.quoteToDelete });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ 
        type: SET_PROCESSING_QUOTES, 
        operation: 'remove', 
        payload: state.quoteToDelete 
      });
      dispatch({ type: SET_QUOTE_TO_DELETE, payload: null });
    }
  }, [state.quoteToDelete, apiRequest, handleApiError]);

  const bulkDeleteQuotes = useCallback(async () => {
    if (!state.selectedQuotes || state.selectedQuotes.length === 0) return;

    dispatch({ 
      type: SET_PROCESSING_QUOTES, 
      operation: 'add', 
      payload: state.selectedQuotes 
    });
    
    setShowBulkDeleteModal(false);

    try {
      // Delete quotes in parallel
      const deletePromises = state.selectedQuotes.map(quoteId => 
        apiRequest(`/quotes/${quoteId}`, {
          method: 'DELETE',
        })
      );

      await Promise.all(deletePromises);

      // Remove all deleted quotes from state
      state.selectedQuotes.forEach(quoteId => {
        dispatch({ type: DELETE_QUOTE, payload: quoteId });
      });

      // Clear selection
      dispatch({ type: SET_SELECTED_QUOTES, operation: 'all', payload: [] });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ 
        type: SET_PROCESSING_QUOTES, 
        operation: 'remove', 
        payload: state.selectedQuotes 
      });
    }
  }, [state.selectedQuotes, apiRequest, handleApiError]);

  // UI Helpers
  const obfuscateEmail = useCallback((email) => {
    if (!email) return "";
    const [username, domain] = email.split("@");
    return username.length > 3
      ? `${username.slice(0, 3)}...@${domain}`
      : `${username}...@${domain}`;
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "approved":
        return "text-green-600 bg-green-100";
      case "rejected":
        return "text-red-600 bg-red-100";
      case "confirmed":
        return "text-blue-600 bg-blue-100";
      case "paid":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-yellow-600 bg-yellow-100";
    }
  }, []);

  const getStatusText = useCallback((status) => {
    switch (status) {
      case "approved":
        return "Approuvé";
      case "rejected":
        return "Rejeté";
      case "confirmed":
        return "Confirmé";
      case "paid":
        return "Payé";
      case "pending":
        return "En attente";
      default:
        return "En attente";
    }
  }, []);

  // Pagination helpers
  const getCurrentQuotes = useCallback(() => {
    const indexOfLastItem = state.currentPage * state.itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - state.itemsPerPage;
    return state.filteredQuotes.slice(indexOfFirstItem, indexOfLastItem);
  }, [state.filteredQuotes, state.currentPage, state.itemsPerPage]);

  const paginate = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= state.totalPages) {
      dispatch({ type: SET_CURRENT_PAGE, payload: pageNumber });
    }
  }, [state.totalPages]);

  const renderPaginationNumbers = useCallback(() => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (state.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= state.totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(
        2,
        state.currentPage - Math.floor(maxVisiblePages / 2)
      );
      let endPage = Math.min(state.totalPages - 1, startPage + maxVisiblePages - 3);

      if (endPage === state.totalPages - 1) {
        startPage = Math.max(2, endPage - (maxVisiblePages - 3));
      }

      if (startPage > 2) {
        pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < state.totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(state.totalPages);
    }

    return pageNumbers;
  }, [state.totalPages, state.currentPage]);

  // Modal controls
  const openRejectModal = useCallback((id) => {
    dispatch({ type: SET_SELECTED_QUOTE_ID, payload: id });
    dispatch({ type: SET_REJECTION_REASON, payload: "" });
    setShowRejectModal(true);
  }, []);

  const openDeleteModal = useCallback((id) => {
    dispatch({ type: SET_QUOTE_TO_DELETE, payload: id });
    setShowDeleteModal(true);
  }, []);

  const openBulkDeleteModal = useCallback(() => {
    if (state.selectedQuotes.length > 0) {
      setShowBulkDeleteModal(true);
    }
  }, [state.selectedQuotes.length]);

  const openImageModal = useCallback((imageData) => {
    dispatch({ type: SET_SELECTED_IMAGE, payload: imageData });
    setShowImageModal(true);
  }, []);

  const downloadImage = useCallback((imageData = null) => {
    const imageToDownload = imageData || state.selectedImage;
    if (!imageToDownload) return;

    try {
      // Create a temporary link element
      const link = document.createElement("a");
      
      // Handle base64 data URLs
      if (imageToDownload.startsWith('data:')) {
        link.href = imageToDownload;
      } else {
        // Handle regular URLs
        link.href = imageToDownload;
      }
      
      // Set download filename with timestamp
      link.download = `logo-${Date.now()}.png`;
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close modal if it's open
      if (state.selectedImage) {
        setShowImageModal(false);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: try to open in new tab
      window.open(imageToDownload, '_blank');
    }
  }, [state.selectedImage]);

  // Selection helpers
  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      dispatch({ 
        type: SET_SELECTED_QUOTES, 
        operation: 'all',
        payload: getCurrentQuotes().map(quote => quote.id) 
      });
    } else {
      dispatch({ type: SET_SELECTED_QUOTES, operation: 'all', payload: [] });
    }
  }, [getCurrentQuotes]);

  const handleQuoteSelect = useCallback((id) => {
    dispatch({ type: SET_SELECTED_QUOTES, operation: 'toggle', payload: id });
  }, []);

  return (
    <DevisContext.Provider
      value={{
        // State
        ...state,
        currentQuotes: getCurrentQuotes(),
        
        // UI state
        showRejectModal,
        setShowRejectModal,
        showDeleteModal,
        setShowDeleteModal,
        showBulkDeleteModal,
        setShowBulkDeleteModal,
        showImageModal,
        setShowImageModal,
        showLogoPriceModal,
        setShowLogoPriceModal,
        isMobile,
        
        // API actions
        fetchQuotes,
        fetchQuoteDetails,
        confirmQuote,
        rejectQuote,
        deleteQuote,
        bulkDeleteQuotes,
        saveLogoPrice,
        
        // UI helpers
        obfuscateEmail,
        getStatusColor,
        getStatusText,
        paginate,
        renderPaginationNumbers,
        handleSelectAll,
        handleQuoteSelect,
        openRejectModal,
        openDeleteModal,
        openBulkDeleteModal,
        openImageModal,
        downloadImage,
        setShowDetailsModal,
        
        // Input handlers
        setSearchTerm: (value) => dispatch({ type: SET_SEARCH_TERM, payload: value }),
        setItemsPerPage: (value) => dispatch({ type: SET_ITEMS_PER_PAGE, payload: value }),
        setRejectionReason: (value) => dispatch({ type: SET_REJECTION_REASON, payload: value }),
        setLogoPriceValue: (value) => dispatch({ type: SET_LOGO_PRICE_VALUE, payload: value }),
        toggleQuantityExpansion: (quoteId) => dispatch({ type: TOGGLE_QUANTITY_EXPANSION, payload: quoteId }),
        setHoveredEmail: (email) => dispatch({ type: SET_HOVERED_EMAIL, payload: email }),
      }}
    >
      {children}
    </DevisContext.Provider>
  );
};

export const useDevis = () => useContext(DevisContext);